import React from "react";
import styled from "styled-components/macro";
import { sourceQuery$data } from "../../../../queries/sources/__generated__/sourceQuery.graphql";
import { useEffectOnce, usePrevious, useWindowSize, useEvent } from "react-use";
import { PICKED_FEW_CARDS } from "../../../../components/fire/xpHooks";
import Captions from "./Captions";
import Controls from "./Controls";
import { getTheme } from "../../../../styled/theme";
import { useLocation } from "react-router";
import LinkExt from "../../../../components/other/LinkExt";
import { useOpenAdsDialog } from "../../../../components/ads/adStack";
import { useMutation } from "relay-hooks";
import { ratingMutation } from "../../../../queries/rating/__generated__/ratingMutation.graphql";
import { ratingEditQL } from "../../../../queries/rating/ratingMutation";
import { ADDCARDHASH } from "../../../main/card/CardNewDialog";
import { useReachedMaximum } from "../../../../components/dialogs/CardsMaxDialog";
import { useGetState } from "components/ReduxProvider";
import { Link } from "react-router-dom";
import PublicIcon from "@mui/icons-material/Public";
import { useViewerQuery } from "queries/viewerQuery";
import { ASSISTANT_HASH } from "components/assistant/AssistantPopup";
import { AUTHOR } from "../../../RootRouter.config";
import { YouTubeProxyPlayer, YouTubeProxyPlayerAPI } from "../../../../components/YouTubeProxyPlayer";
import { EVENT } from "../../../../tools/events";

export const ArticleHeaderH = 240;

const Container = styled.div`
  background: black;
  text-align: center;
`;
const By = styled.div`
  position: absolute;
  z-index: 1;
  width: 100%;
  display: flex;
  font-size: ${({ theme }) => theme.duo.fontSize.smallest};
  color: #bbb;
  padding: 3px 10px 2px;
  background: ${({ theme }) => theme.duo.color.lightGrey}cc;
  backdrop-filter: blur(3px);
  min-height: 18px;
  svg {
    font-size: 13px;
    position: relative;
    top: 3px;
    margin: 0 2px;
  }
  a {
    color: #bbb;
    text-decoration: underline;
  }
`;
const Author = styled.div`
  margin-left: auto;
`;

type Props = {
  source: NonNullable<sourceQuery$data["sourceByUriOrId"]>;
  onShowHeader: (show: boolean) => void;
};

const Video: React.FC<Props> = ({ source, onShowHeader }) => {
  const { viewer, hintWasSeen } = useViewerQuery();
  const [playing, setPlaying] = React.useState(false);
  const openAdsDialog = useOpenAdsDialog();
  const [ratingMutate] = useMutation<ratingMutation>(ratingEditQL);
  const location = useLocation();
  const reachedMaximum = useReachedMaximum();
  const xp = useGetState("xp");
  const yt = React.useRef<YouTubeProxyPlayerAPI>(null);

  useEffectOnce(() => {
    return () => {
      if (viewer) {
        const val = Math.ceil(yt.current?.getCurrentTime() || 0);
        const continueFrom = val / Number(source.videoDuration) > 0.9 ? 0 : val; // >90% => null
        ratingMutate({ variables: { continueFrom, sourceId: source.id } });
      }
    };
  });

  const { width: wWidth } = useWindowSize();
  const maxWidth = parseInt(getTheme().maxWidth.narrow, 10);
  const width = wWidth > maxWidth ? maxWidth : wWidth;
  const height = width * (9 / 16);

  const prevHash = usePrevious(location.hash);
  const eightCards = !hintWasSeen(PICKED_FEW_CARDS) && xp.warn === "tooManyToLearn";
  React.useEffect(() => {
    if (openAdsDialog || location.hash === ADDCARDHASH || location.hash === ASSISTANT_HASH || eightCards) {
      yt.current?.pauseVideo();
      yt.current?.seekTo((yt.current?.getCurrentTime() || 1) - 1, true);
      setTimeout(() => yt.current?.pauseVideo(), 400); // in case blur will turn it up
    }
    if ((prevHash === ADDCARDHASH || prevHash === ASSISTANT_HASH) && !location.hash) {
      yt.current?.playVideo();
    }
  }, [openAdsDialog, location, prevHash, eightCards]);

  useEvent(EVENT.playVideo, (e: { detail: boolean }) => {
    e.detail
      ? yt.current?.playVideo() // play after reading contentTutorialDialog
      : setTimeout(() => yt.current?.pauseVideo(), 1000); // hacky, but it's ok if we don't pause it
  });

  const captions = source.captions && JSON.parse(source.captions);

  return (
    <>
      <Container style={{ height: height }} className="nofilter rr-block rr-ignore">
        <YouTubeProxyPlayer
          ref={yt}
          videoId={source.source || "ERR"}
          startTime={source.rating?.continueFrom || source.videoStart || 0}
          width={width}
          height={height}
          className="rr-block rr-ignore"
          onReady={() => {
            onShowHeader(true);
            if (!reachedMaximum && !(xp.warn === "tooManyToLearn" && !xp.tooManyWarningSeen)) {
              yt.current?.playVideo();
            }
          }}
          onStateChange={(state) => {
            onShowHeader(![1, 3].includes(state) || (xp.warn === "tooManyToLearn" && !xp.tooManyWarningSeen));
            setPlaying(state === 1);
          }}
          onEnd={() => {
            yt.current?.seekTo(0, true);
            setTimeout(() => yt.current?.stopVideo(), 10);
          }}
        />
      </Container>

      <By style={{ top: height }}>
        {!playing && (
          <>
            <div>
              <PublicIcon />
              <LinkExt href={`https://www.youtube.com/watch?v=${source.source}`}>YouTube</LinkExt>
            </div>
            {source.authorName && (
              <Author>
                by <Link to={AUTHOR.url(source.authorName)}>{source.authorName}</Link>
              </Author>
            )}{" "}
          </>
        )}
      </By>

      {yt.current && <Controls yt={yt.current} playing={playing} style={{ top: height }} />}
      {captions && <Captions yt={yt.current} playing={playing} source={source} captions={captions} />}
    </>
  );
};

export default Video;
